/**
 * Client party model — the mirror of the server's `partyState` pushes
 * (SocialModel pattern: adopt the whole push, diff the previous state for
 * toasts, bump `rev` so panels/frames re-render).
 *
 * The server pushes this at 1 Hz while you're in a party (vitals are
 * authoritative), so the diffing has to be idempotent: only real membership
 * changes may toast, never a tick that merely moved someone's HP.
 */
import type { PartyMemberWire, PartyMsg, PartyStateMsg } from "@hyperlight/shared";

/** How long a member's warp keeps the "F — FOLLOW <name>" offer alive. */
export const FOLLOW_WINDOW_MS = 20_000;

export class PartyModel {
  /** Bumped on every adopted push — panels compare and re-render. */
  rev = 0;
  members: PartyMemberWire[] = [];
  /** An invite awaiting my answer, with the wall-clock time it lapses. */
  invite: { from: string; until: number } | null = null;
  /** The most recent party warp — fuel for the follow-jump prompt. */
  jump: { name: string; to: string; at: number } | null = null;
  /** The party's shared rally point (a system every chart marks). */
  rally: { system: string; by: string } | null = null;
  private seq = 0;
  private seeded = false;

  constructor(private handlers: { toast: (line: string) => void }) {}

  /** Stamp the next op seq (monotonic per session — the social idiom). */
  op(m: Omit<PartyMsg, "t" | "seq">): PartyMsg {
    return { t: "party", seq: ++this.seq, ...m };
  }

  get inParty(): boolean {
    return this.members.length > 0;
  }

  get me(): PartyMemberWire | undefined {
    return this.members.find((m) => m.me);
  }

  get isLeader(): boolean {
    return this.me?.leader === true;
  }

  /** Room player ids of party members in MY system — what tints their markers. */
  idsHere(): Set<number> {
    const out = new Set<number>();
    for (const m of this.members) if (!m.me && typeof m.id === "number") out.add(m.id);
    return out;
  }

  /** The live follow-jump offer, or null once it lapses / I'm already there. */
  follow(now: number, mySystem: string): { name: string; to: string } | null {
    const j = this.jump;
    if (!j || now - j.at > FOLLOW_WINDOW_MS || j.to === mySystem) return null;
    if (!this.members.some((m) => m.name === j.name)) return null; // they left
    return { name: j.name, to: j.to };
  }

  apply(msg: PartyStateMsg, now: number): void {
    const first = !this.seeded;
    if (!first) {
      const was = new Set(this.members.map((m) => m.name));
      const is = new Set(msg.members.map((m) => m.name));
      const mine = this.me?.name;
      if (was.size > 0 && is.size === 0) {
        this.handlers.toast("PARTY DISBANDED");
      } else {
        for (const m of msg.members) if (!was.has(m.name)) this.handlers.toast(`${m.name.toUpperCase()} JOINED THE PARTY`);
        for (const name of was) {
          // My own row vanishing IS the disband/kick case above, not a leave.
          if (!is.has(name) && name !== mine) this.handlers.toast(`${name.toUpperCase()} LEFT THE PARTY`);
        }
      }
      if (msg.invite && msg.invite.from !== this.invite?.from) {
        this.handlers.toast(`PARTY INVITE — ${msg.invite.from.toUpperCase()} · O TO ANSWER`);
      }
    }
    if (msg.err) this.handlers.toast(msg.err);
    if (msg.jump) this.jump = { ...msg.jump, at: now };
    if (!first) {
      // The rally flag is the party's loudest signal — announce both planting
      // and striking it, but never on a plain 1 Hz vitals tick.
      const was = this.rally?.system ?? "";
      const is = msg.rally?.system ?? "";
      if (is && is !== was) this.handlers.toast(`RALLY POINT — ${is} · ${msg.rally!.by.toUpperCase()}`);
      else if (!is && was) this.handlers.toast("RALLY POINT CLEARED");
    }
    this.rally = msg.rally ?? null;
    this.members = msg.members;
    this.invite = msg.invite ? { from: msg.invite.from, until: now + msg.invite.ttl } : null;
    this.seeded = true;
    this.rev++;
  }
}
